'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowLeft } from 'lucide-react';
import type { MouseEvent } from 'react';

/**
 * In-page "Back" control. Behaves like the browser's own Back button when there
 * is safe in-app history to return to (so it restores the previous list view
 * with its tab/search/sort/filter intact, since those live in the URL - see
 * lib/use-url-state), and falls back to navigating to `href` - a same-origin
 * parent page - when there is no such history (e.g. the visitor landed directly
 * on this page from a search engine or a shared link).
 *
 * Why both: an in-page control cannot, on its own, prove the previous history
 * entry is same-origin, so calling router.back() blindly could eject a direct
 * visitor off our site. Guarding on window.history.length > 1 means we only step
 * back when the user actually navigated here from within the app; otherwise we
 * go to the known in-app `href`. Either way the visitor stays on-site.
 *
 * Rendered as a real link (href is the fallback target) so it stays crawlable
 * and supports cmd/middle-click "open in new tab"; the click handler only
 * intercepts plain left-clicks to run the history-aware navigation.
 *
 * `href` must be an internal path (starts with "/"). All call sites pass one.
 */
export function BackLink({
  href,
  label = 'Back',
  className,
  iconClassName = 'size-4',
}: {
  /** Same-origin parent page used as the fallback target. Must start with "/". */
  href: string;
  /** Visible text. Defaults to "Back". */
  label?: string;
  className?: string;
  iconClassName?: string;
}) {
  const router = useRouter();

  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    // Let the browser handle modified clicks (new tab/window) and non-left
    // clicks via the real href - don't hijack those.
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return;
    }
    // If we have in-app history to go back to, mimic the browser Back button so
    // the previous list view (and its URL-stored filters) is restored exactly.
    // Otherwise, fall through to the real href (direct landing - no history).
    if (typeof window !== 'undefined' && window.history.length > 1) {
      e.preventDefault();
      router.back();
    }
  }

  return (
    <Link
      href={href}
      onClick={handleClick}
      className={
        className ??
        'inline-flex items-center gap-1.5 text-sm font-medium text-[var(--accent)] hover:underline'
      }
    >
      <ArrowLeft className={iconClassName} aria-hidden="true" />
      <span>{label}</span>
    </Link>
  );
}
